import React from "react";
import { useTranslation } from "react-i18next";
import { formatNumber, hasNumber, validRows } from "../../utils/analyticsFormat";
import styles from "../../OrderAnalyticsPage.module.scss";

function AnswerTable({ columns, rows }) {
  return (
    <div className={styles.answerTableScroll}>
      <table className={styles.answerTable}>
        <thead>
          <tr>
            {columns.map(([key, title]) => (
              <th scope="col" key={key}>
                {title}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, index) => (
            <tr key={index}>
              {columns.map(([key, , render]) => (
                <td key={key}>
                  {render ? render(row[key], row) : typeof row[key] === "string" ? row[key] : "—"}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function AssistantAnswer({ request, data }) {
  const { t, i18n } = useTranslation();
  const n = (value) => formatNumber(value, i18n.language, 2);
  const percent = (value) => (hasNumber(value) ? `${n(value)}%` : "—");
  const text = (value) => (typeof value === "string" ? value : "—");
  const empty = <p>{t("analyticsNew.empty", "Нет данных за выбранный период")}</p>;
  const label = (key, fallback) => t(`analyticsNew.assistant.fields.${key}`, fallback);

  function renderDetails(title, rows) {
    if (!Array.isArray(rows) || !rows.length)
      return (
        <>
          <strong>{title}</strong>
          {empty}
        </>
      );
    if (rows.every((row) => typeof row === "string"))
      return (
        <>
          <strong>{title}</strong>
          <ul>
            {rows.map((row, index) => (
              <li key={index}>{row}</li>
            ))}
          </ul>
        </>
      );
    const records = validRows(rows);
    // The supplied contract has empty detail arrays. Preserve scalar fields until their schema is defined.
    const fields = [...new Set(records.flatMap(Object.keys))].filter(
      (key) =>
        !/id$/iu.test(key) && records.some((row) => typeof row[key] === "string" || hasNumber(row[key])),
    );
    const names = {
      companyName: "Компания",
      expeditor: "ТК",
      route: "Маршрут",
      orderCount: "Заказов",
      totalOrders: "Заказов",
      count: "Количество",
      number: "№",
      price: "Сумма",
      currency: "Валюта",
      avgFirstBidMinutes: "Первый ответ, мин",
      avgResponseMinutes: "Среднее время ответа, мин",
      avgResponseText: "Среднее время ответа",
      totalBids: "Количество ставок",
      tenderParticipations: "Участий",
      tenderWins: "Побед",
      winRate: "Доля побед, %",
      avgPrice: "Средняя ставка",
    };
    return (
      <>
        <strong>{title}</strong>
        {fields.length ? (
          <AnswerTable
            rows={records}
            columns={fields.map((key) => [
              key,
              label(key, names[key] || key),
              (value) => (hasNumber(value) ? n(value) : text(value)),
            ])}
          />
        ) : (
          <p>{t("analyticsNew.assistant.noDetails", "Подробные данные отсутствуют")}</p>
        )}
      </>
    );
  }

  if (request.tool === "orders-by-tender-type")
    return (
      <>
        <p>
          <strong>{t("analyticsNew.assistant.assignmentTitle", "Тендер или прямой проброс")}</strong>
        </p>
        <p>
          {label("total", "Всего заказов")}: <b>{n(data.total)}</b>
        </p>
        <p>
          {t("analyticsNew.assistant.throughTender", "Через тендер")}: <b>{n(data.tender?.count)}</b> (
          {percent(data.tender?.percent)})
        </p>
        <p>
          {t("analyticsNew.assistant.direct", "Прямым пробросом")}: <b>{n(data.direct?.count)}</b> (
          {percent(data.direct?.percent)})
        </p>
      </>
    );

  if (request.tool === "orders-overview")
    return (
      <>
        <p>
          <strong>{t("analyticsNew.assistant.overview", "Обзор заказов")}</strong>
        </p>
        <AnswerTable
          rows={[
            { ...data.auto, mode: t("analyticsNew.overview.auto", "авто") },
            { ...data.air, mode: t("analyticsNew.overview.air", "авиа") },
          ]}
          columns={[
            ["mode", label("mode", "Тип")],
            ["total", label("total", "Всего заказов"), n],
            ["completed", label("completed", "Завершено"), n],
            ["inProgress", label("inProgress", "В работе"), n],
            ["cancelled", label("cancelled", "Отменено"), n],
            ["gmvKzt", label("gmvKzt", "Оборот, ₸"), n],
            ["gmvUsd", label("gmvUsd", "Оборот, $"), n],
            ["gmvEur", label("gmvEur", "Оборот, €"), n],
          ]}
        />
      </>
    );

  if (request.tool === "route-analytics") {
    const normalize = (value) =>
      text(value)
        .toLocaleLowerCase()
        .replace(/\s*(?:→|->)\s*/gu, "→")
        .trim();
    const rows = validRows(data.routes).filter(
      (row) => !request.route || normalize(row.route) === normalize(request.route),
    );
    const priceRows = [...rows]
      .sort((a, b) => (b.orderCount || 0) - (a.orderCount || 0))
      .flatMap((row) => {
        const byCurrency = validRows(row.byCurrency);
        return byCurrency.length
          ? byCurrency.map((prices, index) => ({
              route: index === 0 ? row.route : "",
              orderCount: row.orderCount,
              currency: prices.currency,
              pricedOrders: prices.pricedOrders,
              avgPrice: prices.avgPrice,
              minPrice: prices.minPrice,
              maxPrice: prices.maxPrice,
            }))
          : [row];
      });
    const hasCurrencyBreakdown = rows.some((row) => validRows(row.byCurrency).length > 0);
    const missingCurrency = priceRows.some((row) =>
      [row.avgPrice, row.minPrice, row.maxPrice].some(hasNumber) &&
      !(typeof row.currency === "string" && row.currency.trim()),
    );
    return (
      <>
        <p>
          <strong>{request.route || t("analyticsNew.assistant.routes", "Самые частые маршруты")}</strong>
        </p>
        {rows.length ? (
          <>
            <AnswerTable
              rows={priceRows}
              columns={[
                ["route", label("route", "Маршрут")],
                ["orderCount", label("orderCount", "Заказов"), n],
                ["currency", label("currency", "Валюта")],
                ...(hasCurrencyBreakdown ? [["pricedOrders", label("pricedOrders", "С ценой"), n]] : []),
                ["avgPrice", label("avgPrice", "Средняя ставка"), n],
                ["minPrice", label("minPrice", "Мин."), n],
                ["maxPrice", label("maxPrice", "Макс."), n],
              ]}
            />
            {typeof data.note === "string" && data.note.trim() ? (
              <p className={styles.answerSource}>{data.note}</p>
            ) : hasCurrencyBreakdown ? (
              <p className={styles.answerSource}>
                {t("analyticsNew.assistant.currencyBreakdown", "Заказов — всего по маршруту. Ставки рассчитаны по заказам с ценой отдельно в каждой валюте.")}
              </p>
            ) : null}
            {missingCurrency && (
              <p className={styles.answerSource}>
                {t("analyticsNew.assistant.currencyUnspecified", "Валюта сумм не указана.")}
              </p>
            )}
          </>
        ) : (
          empty
        )}
      </>
    );
  }

  if (request.tool === "fastest-responding-companies")
    return renderDetails(
      t("analyticsNew.assistant.speedTitle", "Скорость первого ответа перевозчиков"),
      data.ranking,
    );

  if (request.tool === "company-info")
    return (
      <>
        <p>
          <strong>{text(data.companyName || request.companyName)}</strong>
        </p>
        <dl className={styles.answerMetrics}>
          {[
            [label("tenderParticipations", "Участий в тендерах"), n(data.tenderParticipations)],
            [label("tenderWins", "Побед"), n(data.tenderWins)],
            [label("winRate", "Доля побед"), percent(data.winRate)],
            [label("avgFirstBidMinutes", "Первый ответ, мин"), n(data.avgFirstBidMinutes)],
            [label("totalOrders", "Всего заказов"), n(data.totalOrders)],
            [label("totalGmv", "Оборот"), n(data.totalGmv)],
            [label("avgPrice", "Средняя ставка"), n(data.avgPrice)],
          ].map(([title, value]) => (
            <div key={title}>
              <dt>{title}</dt>
              <dd>{value}</dd>
            </div>
          ))}
        </dl>
        <p className={styles.answerSource}>
          {t("analyticsNew.assistant.currencyUnspecified", "Валюта сумм не указана.")}
        </p>
        {renderDetails(t("analyticsNew.assistant.mainRoutes", "Основные направления"), data.mainRoutes)}
        {renderDetails(t("analyticsNew.assistant.recentOrders", "Последние заказы"), data.recentOrders)}
      </>
    );
  return null;
}
